import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';
import Modal from 'react-modal';
import { useSelector } from 'react-redux';
import { useLocation, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactTooltip from 'react-tooltip';
import { broadcastStreamShape, heroStreamShape } from '~/helpers/propTypeShapes';
import roundRecordingDuration from '~/helpers/roundRecordingDuration';
import { selectApiBaseUrl } from '~/store';
import '~/style/broadcaststreams.css';
import ClipRecording from '../ClipRecording';
import ConfirmationModal from '../ConfirmationModal';
import BroadcastStreamListItem from './BroadcastStreamListItem';

const customClippingStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    maxWidth: '600px',
    padding: '0',
    textAlign: 'left',
    transform: 'translate(-50%, -50%)',
  },
  overlay: {
    backgroundColor: 'rgb(0,0,0,0.5)',
  },
};

const BroadcastStreams = ({
  broadcast,
  newBroadcastTitle,
  streams,
  reAuth,
  reduceStreamCount,
  trackEvent,
  updateStreams,
}) => {
  const location = useLocation();
  const apiBaseUrl = useSelector(selectApiBaseUrl);

  const [isClipModalOpen, setIsClipModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [recordingToDelete, setRecordingToDelete] = useState(null);
  const [clippingData, setClippingData] = useState(null);

  const closeModal = useCallback(() => {
    setIsClipModalOpen(false);
    setIsConfirmationModalOpen(false);
    setClippingData(null);
    setRecordingToDelete(null);
  }, []);

  const handleEnterKey = useCallback((event) => {
    if (event.keyCode === 13) {
      event.preventDefault();
      event.target.blur();
    }
  }, []);

  const handleClippingRequest = useCallback((recording) => {
    const accurateDuration = roundRecordingDuration(recording.startedAt, recording.stoppedAt);

    setClippingData({
      clippingSourceId: recording.id,
      clippingSourceDuration: accurateDuration,
      clippingSourcePoster: recording.thumbnail,
      clippingSourceType: 'video/mp4',
      clippingSourceURL: recording.outfile,
    });

    setIsClipModalOpen(true);
  }, []);

  const handleDescriptionChange = useCallback(
    async (description, streamId) => {
      const params = {
        method: 'PATCH',
        headers: {
          authorization: sessionStorage.getItem('authToken'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          description,
        }),
      };

      try {
        const response = await fetch(`${apiBaseUrl}/streams/${streamId}`, params);
        if (response.status === 401) {
          trackEvent('401', {
            source: 'streams handleDescriptionChange',
          });
          // eslint-disable-next-line no-console
          console.error('Received 401 response from handleDescriptionChange (BroadcastStreams). Redirecting to login.');
          reAuth(location.pathname, encodeURIComponent(location.search));
          return undefined;
        }

        const updatedStream = await response.json();
        const newStreams = streams.map((stream) => {
          if (stream.id === streamId) {
            return {
              ...stream,
              description: updatedStream.description,
            };
          }
          return stream;
        });

        updateStreams(newStreams);
        return updatedStream;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error: ', error);
        return { error: error.message };
      }
    },
    [location, updateStreams, trackEvent, reAuth, apiBaseUrl, streams]
  );

  const requestDeleteRecording = useCallback((recordingId) => {
    setRecordingToDelete(recordingId);
    setIsConfirmationModalOpen(true);
  }, []);

  const deleteRecording = useCallback(async () => {
    const params = {
      method: 'delete',
      headers: {
        authorization: sessionStorage.getItem('authToken'),
        'Content-Type': 'application/json',
      },
    };

    try {
      const response = await fetch(`${apiBaseUrl}/stream/delete/${recordingToDelete}`, params);
      if (response.status === 401) {
        trackEvent('401', {
          source: 'deleteRecording (BroadcastStreams)',
        });
        // eslint-disable-next-line no-console
        console.error('Received 401 response from deleteRecording (BroadcastStreams). Redirecting to login.');
        reAuth(location.pathname, encodeURIComponent(location.search));
        return;
      }

      const newStreams = streams.filter(({ id: streamId }) => streamId !== recordingToDelete);

      closeModal();
      reduceStreamCount();
      updateStreams(newStreams);
      toast.success('Recording deleted successfully');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('delete recording API call failed. ', error);
      toast.error('Unable to delete recording.');
    }
  }, [
    closeModal,
    updateStreams,
    streams,
    trackEvent,
    reAuth,
    apiBaseUrl,
    reduceStreamCount,
    location,
    recordingToDelete,
  ]);

  if (streams.length === 0) {
    return (
      <div className="content-container broadcast-streams-content-container">
        <ul>
          <li className="not-found" key="NoPastBroadcasts">
            <span>No other recordings available for this broadcast.</span>
          </li>
        </ul>
      </div>
    );
  }

  return (
    <div className="content-container broadcast-streams-content-container">
      {streams.map((stream, index) => (
        <BroadcastStreamListItem
          key={stream.id}
          broadcastHash={broadcast.hash}
          broadcastId={broadcast.id}
          index={index}
          stream={stream}
          newBroadcastTitle={newBroadcastTitle}
          isCurrentUserStreamOwner={stream.user.id === broadcast.user.id}
          handleClippingRequest={handleClippingRequest}
          handleDescriptionChange={handleDescriptionChange}
          handleEnterKey={handleEnterKey}
          requestDeleteRecording={requestDeleteRecording}
        />
      ))}
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        handleOnClose={closeModal}
        handleOnConfirm={deleteRecording}
        resourceDescription="Do you really want to delete this recording? This cannot be undone."
      />

      {clippingData && (
        <Modal isOpen={isClipModalOpen} onRequestClose={closeModal} style={customClippingStyles}>
          <ClipRecording
            APIBaseURL={apiBaseUrl}
            broadcastTitle={broadcast.name}
            clipVidDuration={clippingData.clippingSourceDuration}
            clipVidPoster={clippingData.clippingSourcePoster}
            clipVidType={clippingData.clippingSourceType}
            clipVidURL={clippingData.clippingSourceURL}
            sourceId={clippingData.clippingSourceId}
            closeModal={closeModal}
            location={location}
            reAuth={reAuth}
            trackEvent={trackEvent}
          />
        </Modal>
      )}
      <ReactTooltip id="broadcaststreams-tooltip" effect="solid" type="light" border />
    </div>
  );
};

BroadcastStreams.propTypes = {
  broadcast: broadcastStreamShape.isRequired,
  newBroadcastTitle: PropTypes.string.isRequired,
  streams: PropTypes.arrayOf(heroStreamShape).isRequired,
  reAuth: PropTypes.func.isRequired,
  reduceStreamCount: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  updateStreams: PropTypes.func.isRequired,
};

export default withRouter(BroadcastStreams);
